import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError, BaseResponse } from '@utils/types/shared/base';
import { IS_ON_PREM } from '@utils/constants/app-constants';

export type AdminOnPremSettings = {
  retention_days_default: string | null;
  max_upload_size_mb: string | null;
  default_language: string | null;
  maintenance_mode: string | null;
};

export type AdminOnPremSettingsResponse = BaseResponse<AdminOnPremSettings>;

export const ADMIN_SETTINGS_QUERY_KEY = 'adminOnPremSettings';

/**
 * Hook to fetch on-prem admin settings
 *
 * GET /api/admin/on-prem/settings
 *
 * Returns current system settings (retention days, max upload size, default language, maintenance mode)
 * All values are strings (stored in SystemSetting key-value table). Null means not yet configured.
 */
export const useGetAdminSettings = () => {
  const fetchSettings = async () => {
    const url = `api/admin/on-prem/settings`;
    const response = await Axios.get<AdminOnPremSettingsResponse>(url);
    return response.data;
  };

  return useQuery<AdminOnPremSettingsResponse, AxiosError<BaseError>>(
    [ADMIN_SETTINGS_QUERY_KEY],
    fetchSettings,
    {
      enabled: IS_ON_PREM,
      useErrorBoundary: false,
      staleTime: 5 * 60 * 1000, // Consider stale after 5 minutes
      retry: 1
    }
  );
};
